import { useState, useContext } from 'react';
import { Box, TextField, Button, Typography, useTheme, CircularProgress, IconButton, InputAdornment, Tooltip } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ChatDemo from './Chat/Demo.jsx'; // Import the ChatDemo component
import { UserContext } from './Auth';
import ZigyLogo from './shared/ZigyLogo';

const Login = () => {
  const { setUser } = useContext(UserContext);
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const theme = useTheme();

  const handleLogin = async () => {
    setError(null); // Reset error messages
    setLoading(true);

    try {
      const response = await fetch('/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: username,
          password: password,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.message || 'Login failed, please try again.');
        setLoading(false);
        return;
      }

      const data = await response.json();
      data.settings = JSON.parse(data.settings);
      setUser(data);

      setError(null); // Clear errors on success

      // Navigate to the main app after successful login
      navigate('/chat');
    } catch {
      setError('An unexpected error occurred. Please try again.');
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault(); // Prevent default form submit
    handleLogin(); // Trigger login logic
  };

  return (
    <Box
      sx={{
        height: '100dvh',
        width: '100dvw',
        display: 'flex',
        backgroundColor: 'var(--bg-page-default)',
        justifyContent: 'center', // Center horizontally
      }}
    >
      {/* Layout for Login Form and Chat Demo */}
      <Box 
        sx={{ 
          display: 'flex',
          flex: 1,
          margin: '0 auto', // Center the container
          maxWidth: '1200px', // Reasonable max width
        }}
      >
        {/* Login Form */}
        <Box 
          sx={{ 
            flex: { xs: 1, md: 0.5 },
            display: 'flex',
            flexDirection: 'column'
          }} 
          component="main"
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              padding: '20px',
              backgroundColor: 'var(--bg-page-default)',
            }}
          >
            {/* Screen reader only heading */}
            <Typography component="h1" className="sr-only">
              Login to Zigy
            </Typography>
            {/* Logo */}
            <Box sx={{ display: 'flex', alignItems: 'center',  maxWidth: '7.75rem', marginBottom: '1.5rem' }}>
                <ZigyLogo
                width={'100%'}
                height={'auto'}
                />
            </Box>
            <Box
              component="form"
              onSubmit={handleSubmit}
              aria-label="Login form"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                maxWidth: '400px',
                gap: '16px',
              }}
              noValidate
              autoComplete="off"
            >
              <TextField
                label="Email"
                variant="outlined"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                fullWidth
                required
              />
              <TextField
                label="Password"
                variant="outlined"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={showPassword ? "Hide password" : "Show password"}>
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={{
                            marginRight: '-6px',
                            color: 'text.secondary',
                            transition: 'color 0.2s',
                            '&:hover': {
                              color: 'text.primary',
                            }
                          }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="contained"
                disableElevation={true}
                color="primary"
                type="submit"
                fullWidth
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                sx={{
                  height: '56px',
                  fontSize: '1rem',
                  '&.Mui-disabled': {
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    cursor: 'not-allowed',
                    pointerEvents: 'auto',
                  }
                }}
              >
                {loading ? 'Logging in...' : 'Login'}
              </Button>

              {error && (
                <Typography color="error" variant="body2">
                  {error}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        {/* Chat Demo */}
        <Box
          component="aside"
          aria-label="Chat demo preview"
          sx={{
            flex: { xs: 1, md: 0.5 },
            display: { xs: 'none', md: 'flex' }, // Show at md+
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: theme.palette.mode === 'dark'
              ? theme.palette.background.paper
              : '#f5f5f5', // Theme-aware background for contrast
          }}
        >
          <Box
            sx={{
              width: '100%',
              maxWidth: '26.5625rem', // Restrict max width
              height: '100%', // Allow correct vertical sizing
              maxHeight: '500px', // Cap the height to prevent layout overflow
              border: `1px solid ${theme.palette.border.subtle}`,
              backgroundColor: theme.palette.background.paper, // Theme-aware background for the chat demo
              borderRadius: 2, // Rounded corners
              overflow: 'auto', // Scroll inside the box if content overflows
            }}
          >
            <ChatDemo /> {/* Render the ChatDemo component */}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
